import { useCallback } from 'react'

import styles from './FeedbackPostDrawer.module.scss'
import FeedbackPostDrawerContent from './FeedbackPostDrawerContent'

import LoadingLayout from '~common/LoadingLayout'
import { useFeedbackPostQuery } from '~generated/hooks'
import { TagTypes } from '~generated/types'
import useTags from '~hooks/apollo/useTags'
import useQueryParam from '~lib/hooks/useQueryParam'
import Drawer from '~lib/ui-kit/Drawer'

interface FeedbackPostDrawerProps {
  readOnly?: boolean
  disabled?: boolean
}

export function useFeedbackPostDrawerVisibility() {
  const [feedbackPostId, setFeedbackPostId] = useQueryParam<string>({
    name: 'feedbackPostId',
    replace: true,
    shallow: true,
    persist: ['organizationSlug', 'boardId', 'view'],
  })

  const openDrawer = useCallback(
    (feedbackPostId: string) => {
      setFeedbackPostId(feedbackPostId)
    },
    [setFeedbackPostId]
  )

  const closeDrawer = useCallback(() => {
    setFeedbackPostId(null)
  }, [setFeedbackPostId])

  return {
    feedbackPostId,
    openDrawer,
    closeDrawer,
  }
}

function FeedbackPostDrawer({ readOnly, disabled }: FeedbackPostDrawerProps) {
  const { feedbackPostId, closeDrawer } = useFeedbackPostDrawerVisibility()

  const {
    data: { feedbackPost } = {},
    previousData,
    loading,
  } = useFeedbackPostQuery({
    skip: !feedbackPostId,
    variables: { id: feedbackPostId },
  })

  const { tags } = useTags({
    variables: { type: TagTypes.FEEDBACK_POST },
    fetchPolicy: 'cache-first',
  })

  return (
    <Drawer
      opened={Boolean(feedbackPostId)}
      size={800}
      direction="right"
      onOverlayClick={closeDrawer}
      className={styles.root}
    >
      <LoadingLayout loading={loading} className="pt-10 pb-10">
        <FeedbackPostDrawerContent
          feedbackPost={feedbackPost || previousData?.feedbackPost}
          tags={tags}
          readOnly={readOnly}
          disabled={disabled}
        />
      </LoadingLayout>
    </Drawer>
  )
}

export default FeedbackPostDrawer
